import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  User,
  Lock,
  Bell,
  Shield,
  Palette,
  LogOut,
  ChevronRight,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EditProfileModal } from '@/components/EditProfileModal'
import { ThemeModal } from '@/components/settings/ThemeModal'
import { PasswordModal } from '@/components/settings/PasswordModal'
import { LanguageModal } from '@/components/settings/LanguageModal'
import { useAuth } from '@/hooks/useAuth'

interface SettingItem {
  label: string
  action: () => void
}

interface SettingSection {
  icon: React.ElementType
  title: string
  description: string
  items: SettingItem[]
}

export function SettingsPage() {
  const navigate = useNavigate()
  const { logout, user } = useAuth()
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalContent, setModalContent] = useState({ title: '', description: '' })
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false)
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false)

  const handleLogout = async () => {
    setIsLoggingOut(true)
    await logout()
    navigate('/login')
  }

  const showComingSoon = (feature: string) => {
    setModalContent({
      title: feature,
      description: 'This feature is coming soon! We\'re working hard to bring you the best experience.',
    })
    setModalOpen(true)
  }

  const settingsSections: SettingSection[] = [
    {
      icon: User,
      title: 'Account',
      description: 'Manage your account details and preferences',
      items: [
        {
          label: 'Edit Profile',
          action: () => setIsEditProfileOpen(true)
        },
        {
          label: 'Email & Phone',
          action: () => showComingSoon('Email & Phone')
        },
        {
          label: 'Username',
          action: () => showComingSoon('Username')
        },
      ],
    },
    {
      icon: Lock,
      title: 'Privacy',
      description: 'Control who can see your content',
      items: [
        {
          label: 'Account Privacy',
          action: () => showComingSoon('Account Privacy')
        },
        {
          label: 'Blocked Accounts',
          action: () => showComingSoon('Blocked Accounts')
        },
        {
          label: 'Data & History',
          action: () => showComingSoon('Data & History')
        },
      ],
    },
    {
      icon: Bell,
      title: 'Notifications',
      description: 'Manage your notification preferences',
      items: [
        {
          label: 'Push Notifications',
          action: () => showComingSoon('Push Notifications')
        },
        {
          label: 'Email Notifications',
          action: () => showComingSoon('Email Notifications')
        },
      ],
    },
    {
      icon: Shield,
      title: 'Security',
      description: 'Keep your account secure',
      items: [
        {
          label: 'Password',
          action: () => setIsPasswordModalOpen(true)
        },
        {
          label: 'Two-Factor Auth',
          action: () => showComingSoon('Two-Factor Auth')
        },
        {
          label: 'Login Activity',
          action: () => showComingSoon('Login Activity')
        },
      ],
    },
    {
      icon: Palette,
      title: 'Appearance',
      description: 'Customize how KLASSIC looks',
      items: [
        {
          label: 'Theme',
          action: () => setIsThemeModalOpen(true)
        },
        {
          label: 'Language',
          action: () => setIsLanguageModalOpen(true)
        },
      ],
    },
  ]

  return (
    <div className="space-y-6">
      <h1 className="font-display text-2xl font-bold">Settings</h1>

      {/* Account info summary */}
      <Card>
        <CardContent className="flex items-center gap-4 py-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-2xl font-bold text-primary">
            {user?.displayName?.[0] || 'U'}
          </div>
          <div>
            <p className="font-medium">{user?.displayName}</p>
            <p className="text-sm text-muted-foreground">@{user?.username}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </CardContent>
      </Card>

      {/* Settings sections */}
      {settingsSections.map((section) => (
        <Card key={section.title}>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <section.icon className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg">{section.title}</CardTitle>
                <CardDescription>{section.description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="divide-y">
              {section.items.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className="flex w-full items-center justify-between py-3 text-left transition-colors hover:text-primary"
                  onClick={item.action}
                >
                  <span>{item.label}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Logout */}
      <Card>
        <CardContent className="py-4">
          <Button
            variant="destructive"
            className="w-full"
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            <LogOut className="mr-2 h-4 w-4" />
            {isLoggingOut ? 'Logging out...' : 'Log Out'}
          </Button>
        </CardContent>
      </Card>

      {/* App info */}
      <div className="text-center text-xs text-muted-foreground">
        <p>KLASSIC v0.1.0</p>
        <div className="mt-2 flex justify-center gap-4">
          <button
            type="button"
            onClick={() => showComingSoon('Terms of Service')}
            className="hover:underline"
          >
            Terms
          </button>
          <button
            type="button"
            onClick={() => showComingSoon('Privacy Policy')}
            className="hover:underline"
          >
            Privacy
          </button>
          <button
            type="button"
            onClick={() => showComingSoon('Help Center')}
            className="hover:underline"
          >
            Help
          </button>
        </div>
      </div>

      {/* Coming Soon Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{modalContent.title}</DialogTitle>
            <DialogDescription>{modalContent.description}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setModalOpen(false)}>
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Modal */}
      <EditProfileModal
        open={isEditProfileOpen}
        onOpenChange={setIsEditProfileOpen}
      />

      {/* Theme Modal */}
      <ThemeModal
        open={isThemeModalOpen}
        onOpenChange={setIsThemeModalOpen}
      />

      {/* Password Modal */}
      <PasswordModal
        open={isPasswordModalOpen}
        onOpenChange={setIsPasswordModalOpen}
      />

      {/* Language Modal */}
      <LanguageModal
        open={isLanguageModalOpen}
        onOpenChange={setIsLanguageModalOpen}
      />
    </div>
  )
}
